import React, {useState} from 'react';
import {Box, Button, Dialog, DialogActions, styled, SxProps, TextField} from '@mui/material';
import {scale} from "../../utils";
import theme, {FONTS} from "../../constants/theme";
import ThemedText from "../text";
import {useMediaQuery} from "@mui/system";

interface Props {
  title: string;
  content: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (input: string | never) => void;
  colorStyle: 'red' | 'blue';
  sx?: SxProps;
  contentSx?: SxProps;
  hasTextInput?: boolean;
  placeholder?: string;
  cancelText: string;
  confirmText: string;
}

const ThemedButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'colorStyle' && prop !== 'isMobile'
})<{
  colorStyle: 'red' | 'blue';
  isMobile: boolean;
}>(({ colorStyle, isMobile }) => ({
  borderRadius: scale(30),
  borderWidth: scale(2),
  borderColor: theme.palette.text.primary,
  width: isMobile ? scale(180) : scale(160),
  height: isMobile ? scale(56) : scale(52),
  marginTop: scale(13),
  boxShadow: 'none',
  color: theme.palette.text.primary,
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: colorStyle === 'red' ? theme.palette.error.dark : theme.palette.primary.dark,
  },
  [theme.breakpoints.down('sm')]: {
    width: scale(180),
    height: scale(56),
  },
}));

const slotProps = (colorStyle: 'red' | 'blue') => {
  return {
    input: {
      sx: {
        fontFamily: FONTS.MONTSERRAT,
        fontWeight: 500,
        fontSize: scale(18),
        color: colorStyle === 'blue' ? theme.palette.white.main : theme.palette.ceramic.contrastText,
        height: scale(40),
        width: scale(300),
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        borderRadius: 0,
        '& > input': {
          height: scale(40),
          paddingTop: scale(7),
          paddingLeft: scale(8),
          paddingBottom: scale(6),
          boxSizing: 'border-box',
        }
      },
    },
  }
}

const inputRootStyle = (colorStyle: 'red' | 'blue') => {
  let style
    if (colorStyle === 'blue') {
      style = {
        borderRadius: 0,
        '& .MuiInput-underline:before': {
          borderBottom: '1px solid',
          borderColor: theme.palette.white.main,
        },
        '& .MuiInput-underline:after': {
          borderBottom: '1px solid',
          borderColor: theme.palette.white.main,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottom: '1px solid',
          borderColor: theme.palette.white.main,
        },
      }
    } else {
      style = {
        borderRadius: 0,
        backgroundColor: theme.palette.white.main,
        '& .MuiInput-underline:before': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottom: 'none',
        },
      }
    }
  return style
}


const FullScreenDialog = ({ title, content, open, onClose, onConfirm, sx, contentSx, colorStyle, hasTextInput = false, placeholder, cancelText, confirmText }: Props) => {
  const [inputValue, setInputValue] = useState<string>('')
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDisabled = (): boolean => {
    if (hasTextInput === true) {
      if (colorStyle === 'red') {
        return inputValue.toLowerCase() !== 'elimina'
      } else {
        return !inputValue.trim() || inputValue.length > 250
      }
    }
    return false;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{
        paper: {
          sx: {
          backgroundColor: '#38485599',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: colorStyle === 'red' ? theme.palette.error.main : '#007AC3', // Colore contenitore
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: scale(35),
          paddingLeft: scale(28),
          paddingRight: scale(28),
          paddingBottom: scale(29),
          borderRadius: scale(18),
          textAlign: 'center',
          color: 'white',
          width: scale(500),
          ...sx
        }}
      >
        <ThemedText
          text={title}
          fontWeight={800}
          fontSize={scale(24)}
          fontFamily={FONTS.MONTSERRAT}
          sx={{width: scale(445)}}
        />
        <ThemedText
          text={content}
          fontWeight={600}
          fontSize={scale(18)}
          fontFamily={FONTS.MONTSERRAT}
          sx={{width: scale(372), ...contentSx}}
        />
        {hasTextInput === true && (
          <TextField
            placeholder={placeholder ?? ''}
            variant="standard"
            color={'primary'}
            size={'small'}
            slotProps={slotProps(colorStyle)}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onBlur={(event) => setInputValue(event.target.value)}
            sx={{
              width: scale(300),
              marginTop: scale(20),
              ...inputRootStyle(colorStyle)
            }}
          />
        )}
        <DialogActions sx={{ justifyContent: 'center', mt: 2 }}>
          <ThemedButton variant="outlined" colorStyle={colorStyle} isMobile={isMobile} onClick={onClose}>
            {cancelText}
          </ThemedButton>
          <ThemedButton variant="outlined" colorStyle={colorStyle} isMobile={isMobile} onClick={() => onConfirm(inputValue)} disabled={isDisabled()}>
            {confirmText}
          </ThemedButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FullScreenDialog;
